import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Spoiler from '../../../../../../../../components/Spoiler'
import Field from '../../../../../../../../components/Form/Field'

import { getBillingEvent } from '../../../formatters'

import { selectedTikTokAdAccountSelector } from '../../../../../../../../modules/selectors/adAccounts'

import { BID_PRICE } from '../../../../../../LineItemForms/LineItemTikTokCreate/LineItemTikTokCreateForm/fields'
import {
  ENGAGEMENT,
  LEAD_GENERATION,
  REACH,
  TRAFFIC,
  VIDEO_VIEWS,
  WEB_CONVERSIONS
} from '../../../../../../../../constants/campaigns'
import { OPTIMIZATION_GOAL } from '../../../fields'
import { CLICK, CONVERT, FOLLOWERS, PROFILE_VIEWS } from '../../../optimizationGoalOptions'

import useDrawerFormStyles from '../../../../../../../../styles/common/drawerForms'

const CpaAdvancedSettings = ({
  formik,
  goalOptions = [],
  campaignObjective,
  showInSpoiler = true,
  isOptional = true
}) => {
  const { t } = useTranslation()

  const drawerFormClasses = useDrawerFormStyles()

  const { currency_symbol } = useSelector(selectedTikTokAdAccountSelector)

  const { values } = formik

  const goal = values[OPTIMIZATION_GOAL]

  // Get the billing event
  const billingEvent = getBillingEvent(campaignObjective, goal)

  // Check if the conditions are met for showing cost per conversion cap
  const isConversionCap = billingEvent === 'OCPM'

  const cpaName = useMemo(() => {
    if (isConversionCap) {
      return t('Cost cap')
    } else {
      return campaignObjective.toLowerCase() === REACH ? 'CPM' : 'Cost per action'
    }
  }, [t, isConversionCap, campaignObjective])

  const goalLabel = useMemo(() => {
    if (goal === FOLLOWERS) {
      return 'follower'
    } else if (goal === PROFILE_VIEWS) {
      return 'profile view'
    } else if (goal === CLICK) {
      return 'click'
    } else if (goal === CONVERT) {
      return 'conversion'
    } else {
      const foundOption = goalOptions.find(option => option.value === goal)
      return foundOption?.label.toLowerCase()
    }
  }, [goal, goalOptions])

  const title = useMemo(() => {
    // If conditions are met for conversion cap, show specific text
    if (isConversionCap) {
      return isOptional ? t(`Cost per ${goalLabel} cap (optional)`) : t(`Cost per ${goalLabel} cap`)
    }

    switch (campaignObjective.toLowerCase()) {
      case REACH:
        return isOptional ? t('CPM bid control (optional)') : t('CPM bid control')
      case TRAFFIC:
      case LEAD_GENERATION:
      case WEB_CONVERSIONS:
        return isOptional ? t('Cost per action target value (optional)') : t('Cost per action target value')
      case VIDEO_VIEWS:
      case ENGAGEMENT:
        return isOptional ? t('Bid control (optional)') : t('Bid control')

      default:
        return ''
    }
  }, [t, campaignObjective, isOptional, isConversionCap, goalLabel])

  const description = useMemo(() => {
    // If conditions are met for conversion cap, show specific text
    if (isConversionCap) {
      return isOptional
        ? t(
            `Enter a value to set your maximum cost per ${goalLabel}, or leave it blank to use the maximum delivery bid strategy.`
          )
        : t(`Enter a value to set your maximum cost per ${goalLabel}.`)
    }

    switch (campaignObjective.toLowerCase()) {
      case REACH:
        return t(
          'A bid control will limit the CPM to the amount you set. This might limit the campaign’s delivery if it is too low.'
        )
      case TRAFFIC:
      case LEAD_GENERATION:
      case WEB_CONVERSIONS:
        return isOptional
          ? t(
              `Enter a value to set your target cost per ${goalLabel}, or leave it blank to use the maximum delivery bid strategy.`
            )
          : t(`Enter a value to set your target cost per ${goalLabel}.`)
      case VIDEO_VIEWS:
      case ENGAGEMENT:
        return isOptional
          ? t(
              `Enter a cost per ${goalLabel} value to keep your average cost per ${goalLabel} around or below, or leave it blank to use the maximum delivery bid strategy.`
            )
          : t(`Enter a cost per ${goalLabel} value to keep your average cost per ${goalLabel} around or below.`)
      default:
        return ''
    }
  }, [t, campaignObjective, goalLabel, isOptional, isConversionCap])

  if (showInSpoiler) {
    return (
      <Spoiler title={t('Advanced Settings')}>
        <section className={drawerFormClasses.section}>
          <h4 className={drawerFormClasses.sectionTitle}>{title}</h4>
          <p>{description}</p>
          <Field
            symbol={currency_symbol}
            formik={formik}
            name={BID_PRICE}
            placeholder={cpaName}
            autoComplete="off"
            type="number"
            disablePlaceholderFormatting={true}
          />
        </section>
      </Spoiler>
    )
  } else {
    return (
      <section className={drawerFormClasses.section}>
        <h4 className={drawerFormClasses.sectionTitle}>{title}</h4>
        <p>{description}</p>
        <Field
          symbol={currency_symbol}
          formik={formik}
          name={BID_PRICE}
          placeholder={cpaName}
          autoComplete="off"
          type="number"
          disablePlaceholderFormatting={true}
        />
      </section>
    )
  }
}

export default CpaAdvancedSettings
